import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Grid } from '@mui/material'
import { useReactToPrint } from 'react-to-print'

// App Components
import { DataGridColumns } from 'src/admin/core/components/DataGrid/CustomDataGrid'
import { AdminDataGrid } from 'src/admin/core/components/DataGrid/AdminDataGrid'
import { Page } from 'src/admin/core/components'
import { formatConsecutiveWagerNumbers, generateFilterGraphql } from 'src/admin/core'
import { ContentDataGrid } from 'src/admin/core/components/DataGrid'
import { PrintButton } from 'src/admin/core/components'
import { DownloadButton } from 'src/admin/core/components/Buttons/DownloadButton'
 

// Hooks

import { useAppTranslation, usePagination, useUserClaimActions } from 'src/admin/core/hooks'
import { Filters, FilterModel, initialFilters } from './components/Filters'
import { ClaimActions } from 'src/admin/core/models'
import { Products } from 'src/core/models'
import { AgencyWeeklySales } from '../weeklyBalance/models/AgencyWeeklySales'
 
import { PrintReport, downloadReportAsPDF } from './components/PrintReport'
import { useWorkstationTransactionHistory } from './hooks/useWorkstationTransactionHistory'
 
 

const pageStyle = `
  @page {
    size: 80mm 297mm; 
    margin: 0mm;  
  }
`

export type TransactionData = {
  id: number
  time: string
  type: string
  amount: number
  balance: number
  ticket: string
  wagers?: string
  league: string
  race: string
  date: string
  transaction: string
  transactionType: string
  providerTicketId?: string
  leagueName?: string
  workstationId?: string
  agencyId?: string
  enterpriseId?: string
  lotteryTicketNumber?: string
}

const WorkstationTransactionHistory = () => {
  const { label } = useAppTranslation()
  const [currentFilters, setCurrentFilters] = useState<FilterModel>(initialFilters)
  const { page, pageSize } = usePagination()
  const { validateClaimActions } = useUserClaimActions()
  
  const actions = validateClaimActions('consults.daily-balances', [ClaimActions.CanView, ClaimActions.CanPrint])
  const { results, loading, getWorkstationBalances } = useWorkstationTransactionHistory()
  const [transactionData, setTransactionData] = useState<TransactionData[]>([])
  const [startPrinting, setStartPrinting] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  
  // Define a type for the PrintReport component reference
  type PrintReportRef = {
    getTableRef: () => React.RefObject<HTMLDivElement>
    downloadReport: () => void
  }

  // Use useRef with the correct type
  const componentRef = useRef<HTMLDivElement>(null)
  const printReportRef = useRef<PrintReportRef>(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Memoize getCustomFilters to prevent recreation on every render
  const getCustomFilters = useCallback((filters: FilterModel) => {
    setFromDate(filters.from)
    setToDate(filters.to)
 
    return {
      product: filters.product,
      currency: filters.currency,
      from: filters.from,
      to: filters.to,
      provider: filters.provider,
      enterpriseId: filters.enterpriseId,
      agencyId: filters.agencyId,
      workstationId: filters.workstationId,
    }
  }, [])

  // Handle filter changes from child component
  const handleFilterChange = useCallback((filters: FilterModel) => {
    setCurrentFilters(filters)
  }, [])

  // Handle search button click
  const handleSearch = useCallback((filters: FilterModel) => {
    const customFilters = getCustomFilters(filters)
    if (customFilters) {
      getWorkstationBalances(customFilters)
    }
  }, [getCustomFilters, getWorkstationBalances])

  // Remove automatic API call - now only triggered by search button

  useEffect(() => {
    const data = results.results

    if (data.length > 0) {
      const transactions: TransactionData[] = []

      data.forEach((item: any, index: number) => {
        // Extract transaction data
        const transactionType = item.transactionEvent?.transactionType || item.type || 'Unknown'
        const amount = item.amount || 0
        const date = item.date || ''
        const transaction = item.transaction || ''
        const balance = item.balance || 0 // Use balance calculated in the hook
        
        // Format time from date to 12-hour format with AM/PM (HH:MM:SS:MMM AM/PM)
        const dateObj = new Date(date)
        const hours24 = dateObj.getHours()
        const hours12 = hours24 % 12 || 12 // Convert to 12-hour format (0 becomes 12)
        const ampm = hours24 >= 12 ? 'PM' : 'AM'
        const hours = hours12.toString().padStart(2, '0')
        const minutes = dateObj.getMinutes().toString().padStart(2, '0')
        const seconds = dateObj.getSeconds().toString().padStart(2, '0')
        const milliseconds = dateObj.getMilliseconds().toString().padStart(3, '0')
        const time = `${hours}:${minutes}:${seconds}:${milliseconds} ${ampm}`
        
        // Extract ticket information if available
        let ticket = ''
        let wagers = ''
        let league = ''
        let race = ''
        // Concatenate number and providerTickets with pipe for lottery ticket display
        const transactionNumber = item.transactionEvent?.number || ''
        const providerTickets = item.transactionEvent?.providerTickets || ''
        const lotteryTicketNumber = transactionNumber && providerTickets 
          ? `${transactionNumber}|${providerTickets}` 
          : (transactionNumber || providerTickets)
        
        if (item.ticket?.horseRacesWagerCollections?.length > 0) {
          const collection = item.ticket.horseRacesWagerCollections[0]
          ticket = collection.providerTicketId || ''
          
          // Concatenate league key, name, and race number
          const leagueKey = collection.event?.league?.key || ''
          const leagueName = collection.event?.league?.name || ''
          const raceNumber = collection.race || ''
          const leagueDisplay = leagueKey && leagueName ? `${leagueKey} ${leagueName}` : (leagueName || leagueKey)
          league = raceNumber ? `${leagueDisplay} #${raceNumber}` : leagueDisplay
          
          // Concatenate all wagers with line breaks
          if (collection.wagers && collection.wagers.length > 0) {
            wagers = collection.wagers
              .map((wager: any) => {
                // Extract the numbers part from displayValue (everything after the last space)
                const lastSpaceIndex = wager.displayValue.lastIndexOf(' ')
                const numbersOnly = lastSpaceIndex !== -1 
                  ? wager.displayValue.substring(lastSpaceIndex + 1) 
                  : wager.displayValue
                const poolType = lastSpaceIndex !== -1 
                  ? wager.displayValue.substring(0, lastSpaceIndex) 
                  : ''
                
                // Format consecutive numbers
                const formattedNumbers = formatConsecutiveWagerNumbers(numbersOnly)
                
                return `$${wager.bet} ${poolType} ${formattedNumbers}`
              })
              .join('\n')
          }
        }
        
        transactions.push({
          id: index,
          time: time,
          type: label(transactionType),
          amount: amount,
          balance: balance,
          ticket: ticket,
          wagers: wagers,
          league: league,
          race: race,
          date: date,
          transaction: transaction,
          transactionType: label(transactionType),
          providerTicketId: ticket,
          leagueName: league,
          workstationId: item.workstationId || '',
          agencyId: item.agencyId || '',
          enterpriseId: item.enterpriseId || '',
          lotteryTicketNumber: lotteryTicketNumber
        })
      })

      setTransactionData(transactions)
    } else {
      setTransactionData([])
    }
  }, [results.results])

  const isLotteryTicket = currentFilters.product === 'LOTTERY_TICKET'

  const getColumnDefinitions = useCallback((): DataGridColumns => {
    const columns: DataGridColumns = {
      HORA: { 
        field: 'time', 
        contentType: 'custom', 
        headerAlign: 'left', 
        align: 'left',
        width: 140,
        minWidth: 140,
        maxWidth: 140,
        flex: 0,
        renderCell: (params: any) => <span>{params.value}</span>
      },
      TIPO: { 
        field: 'type', 
        contentType: 'custom', 
        headerAlign: 'left', 
        align: 'left', 
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        flex: 0,
        renderCell: (params: any) => <span>{label(params.value)}</span>
      },
      MONTO: { 
        field: 'amount', 
        contentType: 'currency_with_decimals', 
        headerAlign: 'right', 
        align: 'right',
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        flex: 0,
      },
      BALANCE: { 
        field: 'balance', 
        contentType: 'currency_with_decimals', 
        headerAlign: 'right', 
        align: 'right',
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        flex: 0,
      },
      TICKET: { 
        field: 'ticket', 
        contentType: 'custom', 
        headerAlign: 'left', 
        align: 'left',
        flex: 1,
        minWidth: 200,
        renderCell: (params: any) => {
          const row = params.row
          
          // For LOTTERY_TICKET, show lotteryTicketNumber instead
          if (isLotteryTicket) {
            return (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                padding: '8px 0',
              }}>
                <span style={{ fontWeight: 500 }}>{row.lotteryTicketNumber || ''}</span>
              </div>
            )
          }
          
          return (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              padding: '8px 0',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.5',
              wordBreak: 'keep-all',
              overflowWrap: 'normal'
            }}>
              <span style={{ fontWeight: 500 }}>{row.ticket}</span>
              {row.wagers && row.wagers.split('\n').map((wager: string, idx: number) => {
                // Split by / and insert word break opportunities after each slash
                const parts = wager.split('/')
                return (
                  <span 
                    key={idx} 
                    style={{ 
                      fontSize: '0.85em', 
                      color: '#666', 
                      marginTop: '2px',
                      display: 'block',
                      whiteSpace: 'normal',
                      wordBreak: 'normal',
                      overflowWrap: 'normal'
                    }}
                  >
                    {parts.map((part, i) => (
                      <React.Fragment key={i}>
                        <span style={{ whiteSpace: 'nowrap' }}>{part}</span>
                        {i < parts.length - 1 && (
                          <>
                            /<wbr />
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </span>
                )
              })}
            </div>
          )
        }
      },
    }
    
    // Only add HIPODROMO column if product is not LOTTERY_TICKET
    if (!isLotteryTicket) {
      columns.HIPODROMO = { field: 'league', contentType: 'string', headerAlign: 'left', align: 'left' }
    }
    
    return columns
  }, [isLotteryTicket])

  const handlePrint = useCallback(() => {
    setIsProcessing(true)
    setStartPrinting(true)
    setIsProcessing(true)
    setTimeout(() => {
      printReport()
    }, 500)
  }, [])

  const handleDownload = useCallback(() => {
    setIsProcessing(true)
    setFromDate(currentFilters.from)
    setToDate(currentFilters.to)
    setStartPrinting(true)

    // Wait for the component to be rendered
    setTimeout(() => {
      try {
        if (printReportRef.current) {
          // Use the new downloadReport method directly
          printReportRef.current.downloadReport()
          setIsProcessing(false)
          setStartPrinting(false)
          return
        } else {
          // Alternative method: search for elements directly in the DOM
          const tableRefElement = componentRef.current?.querySelector('[data-ref="tableRef"]')

          if (tableRefElement) {
            // Create temporary refs to pass to the function
            const tableRef = { current: tableRefElement as HTMLElement }
            // Give a little more time to ensure elements are fully rendered
            setTimeout(() => {
              downloadReportAsPDF(
                tableRef,
                null,
                `workstation-history-${currentFilters.from}`,
                currentFilters.from,
                currentFilters.to,
                label(currentFilters.provider),
                currentFilters.enterpriseId,
                transactionData,
                label,
                {
                  productObj: currentFilters.productObj,
                  enterpriseObj: currentFilters.enterpriseObj,
                  agencyObj: currentFilters.agencyObj,
                  workstationObj: currentFilters.workstationObj,
                  providerObj: currentFilters.providerObj
                },
                isLotteryTicket
              )
              setIsProcessing(false)
              setStartPrinting(false) // Remove the temporary component after download
            }, 300)
            return
          } else {
            // Handle error: Could not find table element for PDF download
            alert('Error generating PDF: Could not find necessary elements.')
            setIsProcessing(false)
            setStartPrinting(false) // Ensure we remove the component even on error
          }
        }
      } catch (error) {
        // Handle error: Error trying to generate PDF
        alert('Error generating PDF. Please try again.')
        setIsProcessing(false)
        setStartPrinting(false) // Ensure we remove the component even on error
      }
    }, 1000) // We reduce the initial time but add additional times at critical steps
  }, [currentFilters])

  const printReport = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: pageStyle,
    onAfterPrint: () => {
      window.focus()
      setStartPrinting(false)
      setIsProcessing(false)
    },
    onBeforePrint: () => {
      setStartPrinting(false)
    },
  })

  return (
    <Page
      hasAccess={actions.canView}
      tabTitle={label('ESTACION_TRANSACTION_HISTORY_TITULO')}
      pageTitle={label('ESTACION_TRANSACTION_HISTORY_TITULO')}
      actions={
        actions.canPrint ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <DownloadButton onClick={handleDownload} disabled={transactionData.length === 0 || isProcessing} />
            <PrintButton onClick={handlePrint} disabled={transactionData.length === 0 || isProcessing} />
          </Box>
        ) : null
      }
    >
      <Grid item xs={12}>
        <Filters 
          onChange={handleFilterChange} 
          onSearch={handleSearch}
          isLoading={loading}
        />
      </Grid>
      <Grid item xs={12}>
        <ContentDataGrid width="100%">
          <AdminDataGrid
            columns={getColumnDefinitions()}
            data={transactionData}
            loading={loading}
            hideFooterPagination={true}
            rowHeight="auto"
          />
        </ContentDataGrid>
        {startPrinting && (
          <Grid ref={componentRef}>
            <PrintReport
              ref={printReportRef}
              data={transactionData}
              fromDate={fromDate}
              toDate={toDate}
              provider={label(currentFilters.provider)}
              enterpriseName={currentFilters.enterpriseId}
              hideHipodromo={isLotteryTicket}
              metadata={{
                productObj: currentFilters.productObj,
                enterpriseObj: currentFilters.enterpriseObj,
                agencyObj: currentFilters.agencyObj,
                workstationObj: currentFilters.workstationObj,
                providerObj: currentFilters.providerObj
              }}
            />
          </Grid>
        )}
      </Grid>
    </Page>
  )
}

const groupByWorkstationId = (data: any) => {
  const groupedByWorkstationId = data.reduce((result, item) => {
    const workstationName = item.key.workstation?.name
    const agencyId = item.key.workstation?.agency?.id
    // Create a unique key combining workstation name and agency ID to handle cases where
    // multiple workstations might have the same name in different agencies
    const workstationKey = `${workstationName}_${agencyId}`
    
    if (workstationName && agencyId) {
      if (!result[workstationKey]) {
        result[workstationKey] = []
      }
      result[workstationKey].push(item)
    }
    return result
  }, {})

  return groupedByWorkstationId
}

const calculateAmounts = (data: AgencyWeeklySales[]) => {
  let sales = 0
  let cancelled = 0
  let payments = 0
  let totalSurcharge = 0

  let totalAmount = 0

  // totalSurcharge = sales - surcharge cancellation
  data.forEach((item) => {
    const {
      amount,
      surcharge,
      key: { transactionType },
    } = item
    if (transactionType === 'SALE') {
      sales += amount + surcharge
      totalSurcharge += surcharge
    } else if (transactionType === 'CANCELLATION') {
      cancelled += amount + surcharge
      totalSurcharge -= surcharge
    } else if (transactionType === 'PAYMENT') {
      payments += amount
    }
  })

  totalAmount = sales - cancelled - payments
  return {
    sales,
    cancelled,
    payments,
    totalAmount,
    totalSurcharge,
  }
}

export default WorkstationTransactionHistory
